/**
 * EN / ES translations for the AI agents homepage, the audit intake modal,
 * the agent simulator, and the /vs-* comparison pages.
 * Imported and merged into the main translations object.
 */
export const aiAgentsTranslations = {
  en: {
    aiAudit: {
      cta: "Book Your AI Operations Audit ($750)",
      ctaShort: "Book AI Audit",
      modalTitle: "Book your AI Operations Audit",
      modalSubtitle:
        "Tell us a little about your business. We'll reply within one business day to schedule your audit.",
      nameLabel: "Your name",
      businessLabel: "Business name",
      tradeLabel: "Industry",
      tradeOptions: [
        "Construction & Manufacturing",
        "Legal Services",
        "Medical & Healthcare",
        "Retail & E-Commerce",
        "Real Estate & Property Management",
        "Local Services & Beauty",
        "Other",
      ],
      teamSizeLabel: "Team size",
      teamSizeOptions: ["1–9 people", "10–20 people", "21–50 people", "50+ people"],
      toolsLabel: "Tools you use today",
      toolsPlaceholder: "e.g. QuickBooks, HubSpot, Google Drive",
      headacheLabel: "Biggest admin headache",
      headachePlaceholder: "e.g. Pulling weekly numbers takes my office manager all of Monday.",
      selectPlaceholder: "Select…",
      submit: "Send audit request",
      cancel: "Cancel",
      close: "Close",
      priceNote: "AI Operations Audit: $750 one-time. No commitment to further work.",
      sentTitle: "Your email app should be opening",
      sentText:
        "We've drafted the request for you. Just hit send. If nothing opened, email us directly at",
      done: "Done",
      emailSubject: "AI Operations Audit request",
      emailIntro: "Hi Kranbos, I'd like to book an AI Operations Audit.",
    },
    aiSimulator: {
      windowTitle: "Kranbos Agent Preview",
      suggestionsLabel: "Try a sample question:",
      placeholder: "Ask about past quotes, job specs, or invoices…",
      send: "Send",
      typing: "Checking your files and tools…",
      userLabel: "You",
      agentLabel: "Kranbos Agent",
      sourceLabel: "Source",
      exampleNote: "Sample business data for illustration.",
      fallback:
        "This preview only answers the sample questions above. In your business, the agent answers from your own documents, price sheets, and software, and always shows where each answer came from.",
      scenarios: [
        {
          id: "warranty",
          chip: "Warranty lookup",
          question:
            "What's the warranty on commercial HVAC installs in our 2024 service agreement?",
          answer: "Per section 4.2 of your 2024 Commercial Service Agreement:",
          bullets: [
            "Parts & labor: 2-year full coverage",
            "Compressor: 5-year limited warranty",
            "Requires the annual maintenance visit on file",
          ],
          followUp: "",
          source: "Google Drive › Contracts › 2024 Commercial Service Agreement.pdf",
          keywords: ["warranty", "coverage", "guarantee"],
        },
        {
          id: "unbilled",
          chip: "Unbilled jobs",
          question: "Which jobs finished last week still haven't been invoiced?",
          answer: "3 completed jobs from last week have no invoice in QuickBooks yet:",
          bullets: [
            "#4471 Water heater replacement (Rivera): $2,380",
            "#4475 Panel upgrade (Oak St. Dental): $4,150",
            "#4482 Drain repair (Chen): $640",
          ],
          followUp: "Total unbilled: $7,170. Want me to draft the invoices?",
          source: "Jobber (completed jobs) + QuickBooks (invoices)",
          keywords: ["invoice", "invoiced", "unbilled", "billing", "bill"],
        },
        {
          id: "report",
          chip: "Weekly owner report",
          question: "Give me this week's summary for the Monday meeting.",
          answer: "Here's your week at a glance (Mon–Fri):",
          bullets: [
            "42 jobs completed, 5 callbacks (down 12% vs. last week)",
            "Invoiced: $61,300 · Outstanding over 30 days: $8,900",
            "Top crew by completed jobs: Team B (14)",
          ],
          followUp: "The full report lands in your inbox every Monday at 7:00 AM.",
          source: "ServiceTitan + QuickBooks",
          keywords: ["report", "summary", "week", "weekly", "numbers", "meeting"],
        },
        {
          id: "pricing",
          chip: "Price sheet",
          question: "What do we charge for a 50-gallon water heater swap with haul-away?",
          answer: "From your 2024 Residential Price Sheet:",
          bullets: [
            "50-gal gas water heater, installed: $1,850",
            "Haul-away & disposal: $95",
            "Permit (if required): billed at cost",
          ],
          followUp: "Maintenance plan members get 10% off labor.",
          source: "Google Drive › Pricing › 2024 Residential Price Sheet.xlsx",
          keywords: ["price", "pricing", "charge", "cost", "quote", "heater"],
        },
      ],
    },
    aiHome: {
      hero: {
        beforeTitle: "Manual Work & Chaos",
        beforeAlt:
          "A stressed business owner at a cluttered desk with messy spreadsheets, scattered papers, and sticky notes",
        afterTitle: "Automated Workflow",
        afterAlt:
          "The same business owner relaxed with a coffee, looking at a clean Kranbos dashboard showing quotes generated and automated tasks complete",
        title: "Stop copy-pasting, manually calculating, and hunting through files.",
        titleAccent: "hunting through files.",
        badge: "Custom AI trained on your business data",
        subtitle:
          "We build custom AI that connects to your internal files and software—handling quotes, reports, and routine admin automatically.",
      },
      industries: {
        title: "Built for growing businesses in any industry",
        titleAccent: "any industry",
        items: [
          { icon: "construction", label: "Construction & Manufacturing" },
          { icon: "gavel", label: "Law Firms & Legal Services" },
          { icon: "medical_services", label: "Medical & Healthcare Practices" },
          { icon: "storefront", label: "Retail & E-Commerce" },
          { icon: "apartment", label: "Real Estate & Property Management" },
          { icon: "spa", label: "Local Services & Beauty" },
        ],
      },
      value: {
        title: "Generic AI gives you generic answers. Kranbos builds AI trained on your business.",
        eyebrow: "Why Kranbos",
        titleAccent: "trained on your business.",
        subtitle:
          "Generic AI searches the public internet.\nKranbos builds AI trained exclusively on your internal files.",
        featureHeader: "Feature",
        genericHeader: "Off-the-Shelf AI (like ChatGPT)",
        kranbosHeader: "Custom Kranbos Solution",
        rows: [
          {
            feature: "Data Source",
            generic: "Public web info (often generic or wrong for your business)",
            kranbos: "Your actual files, spreadsheets, PDFs, and internal software",
          },
          {
            feature: "Output Type",
            generic: "Text responses in a chat box",
            kranbos: "Finished spreadsheets, PDF quotes, custom reports, & web forms",
          },
          {
            feature: "Workflow Fit",
            generic: "Requires manual prompt writing every time",
            kranbos: "Triggers automatically inside your daily operations",
          },
          {
            feature: "Data Privacy",
            generic: "Public cloud training",
            kranbos: "Strictly private, secure, and isolated to your business",
          },
        ],
        privacyNote:
          "Every Kranbos solution is trained in a secure, private environment isolated to your business. Your data is never shared with other clients or used to train public AI models.",
      },
      useCases: {
        title: "How businesses use Kranbos to save 10+ hours a week",
        eyebrow: "Use cases",
        titleAccent: "10+ hours a week",
        items: [
          {
            icon: "request_quote",
            title: "Quoting & CRM Sync",
            tag: "Saves 5+ hrs/wk",
            points: [
              "Auto-reads price sheets and fills client intake forms.",
              "Connects directly to CRMs (HubSpot, Salesforce, Jobber) to generate instant PDF quotes.",
            ],
          },
          {
            icon: "query_stats",
            title: "Automated Executive Reporting",
            tag: "Instant Insights",
            points: [
              "Pulls live data from project databases across past years.",
              "Adjusts automatically for inflation, project complexity, and exchange rates.",
            ],
          },
          {
            icon: "manage_search",
            title: "Internal File Search",
            tag: "Zero Search Time",
            points: [
              "Turns contracts, SOPs, and records into a private search database.",
              "Get instant, verified answers sourced strictly from your uploaded files.",
            ],
          },
        ],
      },
      howItWorks: {
        title: "From messy spreadsheets to custom AI in 4 simple steps",
        eyebrow: "How it works",
        titleAccent: "in 4 simple steps",
        stepLabel: "Step",
        steps: [
          {
            icon: "cable",
            title: "Connect Your Software & Documents",
            text: "We link Kranbos to your existing tools—Google Drive, Excel, CRMs, PDFs, and databases. No migration or system changes required.",
          },
          {
            icon: "fact_check",
            title: "We Train & Audit Your Private AI",
            text: "We index your data into an isolated database, ensuring the AI only pulls verified info from your actual records.",
          },
          {
            icon: "account_tree",
            title: "Custom Workflow Build",
            text: "We set up your custom triggers—whether generating quotes via forms, running historical reports, or enabling internal search.",
          },
          {
            icon: "groups",
            title: "Team Onboarding & Hand-off",
            text: "We train your team with plain-English instructions so everyone knows how to save hours on day one.",
          },
        ],
      },
      security: {
        title: "Your business data stays 100% private and secure",
        eyebrow: "Security & privacy",
        titleAccent: "100% private and secure",
        items: [
          {
            icon: "block",
            title: "Zero Public Model Training",
            text: "Your internal files and price sheets are NEVER used to train public AI models like ChatGPT.",
          },
          {
            icon: "cloud_lock",
            title: "Isolated Private Cloud",
            text: "Your data lives in a secure, encrypted database dedicated strictly to your company.",
          },
          {
            icon: "admin_panel_settings",
            title: "Strict Access Controls",
            text: "You control exactly who on your team has permission to access specific data.",
          },
        ],
      },
      faq: {
        title: "Frequently Asked Questions",
        eyebrow: "FAQ",
        titleAccent: "Questions",
        items: [
          {
            q: "Will my team need to learn complicated technical tools?",
            a: "Not at all. If your team can fill out a web form, they can use Kranbos. We handle all technical setup.",
          },
          {
            q: "What if our spreadsheets or files are messy?",
            a: "That’s normal. During our Deep AI Operations Audit, we clean, organize, and structure your data for 100% accuracy.",
          },
          {
            q: "Do we have to replace the software we already use?",
            a: "No! Kranbos connects directly to your existing tools (HubSpot, Jobber, Excel, Google Workspace, etc.).",
          },
          {
            q: "How long does the setup process take?",
            a: "Most custom implementations are fully built, tested, and handed over within 2 to 3 weeks.",
          },
        ],
      },
      engagement: {
        title: "Simple, predictable pricing to automate your business operations",
        eyebrow: "Pricing",
        titleAccent: "predictable",
        featured: "Core engagement",
        phases: [
          {
            label: "Step 1",
            title: "Deep AI Operations Audit",
            price: "$750",
            priceNote: "100% credited toward your implementation build",
            text: "A hands-on review of your manual bottlenecks, files, and software, delivered as a custom automation blueprint.",
            features: [
              "Detailed process mapping",
              "Custom AI blueprint & projected ROI",
              "$750 fully credited toward your build",
            ],
            cta: "Book AI Audit",
          },
          {
            label: "Step 2",
            title: "Custom Implementation",
            price: "$3,500 – $7,500",
            priceNote: "One-time build",
            text: "We build, test, and integrate your custom solution using your company’s files, pricing data, and software.",
            features: [
              "Custom workflows built on your data",
              "Integrated with your docs, sheets & databases",
              "End-to-end testing & full team training",
            ],
            cta: "Schedule a Service Call",
          },
          {
            label: "Step 3",
            title: "Managed System & Support",
            price: "$950 / month",
            priceNote: "Ongoing",
            text: "We keep your system running fast, secure, and fully updated as your business grows and your files expand.",
            features: [
              "Continuous data syncs as you grow",
              "Maintenance & performance optimization",
              "Priority support & regular updates",
            ],
            cta: "Manage Your Plan",
          },
        ],
        implementationSubject: "Custom Implementation service call",
        retainerSubject: "Managed System & Support plan",
      },
      finalCta: {
        title: "Find out what custom AI can take off your plate.",
        titleAccent: "take off your plate.",
        text: "Book a quick, 20-minute operations audit. We’ll review your current software and show you where manual work can be eliminated completely.",
      },
    },
    comparisons: {
      hubEyebrow: "Why Kranbos",
      hubTitle: "How does Kranbos compare?",
      hubSubtitle:
        "See why off-the-shelf AI tools fall short for day-to-day small business operations.",
      readMore: "Read comparison",
      otherTitle: "More comparisons",
      footerTitle: "Comparisons",
      eyebrow: "Comparison",
      seeDifference: "See the difference",
      summaryTitle: "The short answer",
      scenarioEyebrow: "Same question, two answers",
      askedBy: "Your technician asks",
      kranbosLabel: "Kranbos Agent",
      tableTitle: "Side by side",
      tableFeature: "What matters",
      tableKranbos: "Kranbos",
      reasonsTitle: "Why it matters for your business",
      faqTitle: "Common questions",
      ctaEyebrow: "Next step",
      backHome: "Back to home",
      exampleNote: "Illustrative example with sample data.",
      disclaimer:
        "ChatGPT is a trademark of OpenAI. Gemini and Google Workspace are trademarks of Google LLC. Kranbos is independent and not affiliated with or endorsed by either company.",
      pages: {
        genericAi: {
          path: "/vs-generic-ai",
          navLabel: "vs. Generic AI Models",
          cardTitle: "Generic AI Models vs. Kranbos",
          cardText: "Why general-purpose AI gives broad answers instead of your exact business data.",
          metaTitle: "Generic AI vs. Kranbos | Custom AI Agents for Small Businesses",
          metaDescription:
            "Generic AI models don't know your prices, contracts, or job history. See how a Kranbos custom AI agent answers from your own business documents and tools.",
          title: "Generic AI knows the internet. Kranbos knows your business.",
          subtitle:
            "General AI models are trained on public information. They've never seen your price sheet, your service agreements, or last month's jobs, so they fill the gaps with confident guesses.",
          competitorName: "Generic AI",
          summaryCompetitorTitle: "Generic AI is fine for",
          summaryCompetitorItems: [
            "Drafting a general email or social post",
            "Explaining an industry concept",
            "Brainstorming marketing ideas",
          ],
          summaryKranbosTitle: "Kranbos is built for",
          summaryKranbosItems: [
            "Answers based on your own documents and records",
            "Live data from the tools your team already uses",
            "Showing exactly where every answer came from",
          ],
          scenarioQuestion: "What's our warranty on a commercial rooftop unit we installed in 2024?",
          competitorAnswer:
            "Commercial rooftop units typically carry a 1-year labor warranty and a 5 to 10-year parts warranty, depending on the manufacturer. Check your paperwork for details.",
          competitorNote: "Sounds reasonable, but it's an industry average, not your contract.",
          kranbosAnswer: "Per section 4.2 of your 2024 Commercial Service Agreement:",
          kranbosBullets: [
            "Parts & labor: 2-year full coverage",
            "Compressor: 5-year limited warranty",
          ],
          kranbosSource: "Google Drive › Contracts › 2024 Commercial Service Agreement.pdf",
          rows: [
            {
              label: "Knows your prices, contracts & policies",
              competitor: "No. Answers from general public information",
              kranbos: "Yes. Answers from your own documents",
            },
            {
              label: "Sees your live jobs, invoices & schedules",
              competitor: "No access to your software",
              kranbos: "Connected to the tools you already use",
            },
            {
              label: "Shows where each answer came from",
              competitor: "Rarely, and it can't point to your files",
              kranbos: "Every answer cites the file or record",
            },
            {
              label: "When it doesn't know",
              competitor: "Often guesses with confidence",
              kranbos: "Says so and points to who can help",
            },
            {
              label: "Set up for your team & trade",
              competitor: "One-size-fits-all",
              kranbos: "Built around your workflows",
            },
          ],
          reasons: [
            {
              icon: "gpp_maybe",
              title: "Wrong answers cost real money",
              text: "A made-up warranty term or price quoted on site turns into a refund, a callback, or an unhappy customer.",
            },
            {
              icon: "folder_managed",
              title: "Your knowledge is already written down",
              text: "Contracts, price sheets, manuals, and job notes hold the answers. Kranbos makes them instantly findable.",
            },
            {
              icon: "verified",
              title: "Trust you can check",
              text: "Because every answer shows its source, your team can verify it in one tap instead of calling the office.",
            },
          ],
          faqs: [
            {
              q: "Can't I just upload my documents to a generic AI tool?",
              a: "You can for one-off questions, but someone has to keep uploading the latest versions, and each person gets different results. Kranbos keeps your documents and software connected and current for the whole team.",
            },
            {
              q: "Is my business data used to train public AI models?",
              a: "No. Your agent is set up for your business only, and your documents and records are not used to train public models.",
            },
            {
              q: "Do my technicians need to learn a new app?",
              a: "No. We deliver the agent where your team already works, like text, chat, or email, so there's nothing new to learn.",
            },
          ],
          ctaTitle: "Get answers from your business, not the internet",
          ctaText:
            "Start with a $750 AI Operations Audit. We'll map where your team loses time and show you exactly what a custom agent would answer.",
        },
        chatgpt: {
          path: "/vs-chatgpt",
          navLabel: "vs. ChatGPT",
          cardTitle: "ChatGPT vs. Kranbos",
          cardText: "Why copy-pasting into ChatGPT falls short for real daily team workflows.",
          metaTitle: "ChatGPT vs. Kranbos | AI Agents Connected to Your Business Tools",
          metaDescription:
            "ChatGPT is great for one-off tasks, but copy-pasting job data every day doesn't scale. See how Kranbos connects live to QuickBooks, Jobber, ServiceTitan and more.",
          title: "ChatGPT is a great assistant. It isn't connected to your business.",
          subtitle:
            "To get a useful answer from ChatGPT, someone has to find the right data, copy it out of your software, paste it in, and double-check the result. Every day. Kranbos connects to your tools directly, so the answers are already there.",
          competitorName: "ChatGPT",
          summaryCompetitorTitle: "ChatGPT is great for",
          summaryCompetitorItems: [
            "Writing and polishing one-off emails",
            "Summarizing a document you paste in",
            "Individual research and brainstorming",
          ],
          summaryKranbosTitle: "Kranbos is built for",
          summaryKranbosItems: [
            "Daily team operations, not one-off chats",
            "Live connections to your job, accounting & file tools",
            "Consistent answers for everyone on the team",
          ],
          scenarioQuestion: "Which jobs finished last week still haven't been invoiced?",
          competitorAnswer:
            "I don't have access to your job management or accounting systems. If you export last week's completed jobs and your invoice list and paste them here, I can compare them for you.",
          competitorNote:
            "Now someone has to export two reports, paste them in, and check the result.",
          kranbosAnswer: "3 completed jobs from last week have no invoice in QuickBooks yet:",
          kranbosBullets: [
            "#4471 Water heater replacement (Rivera): $2,380",
            "#4475 Panel upgrade (Oak St. Dental): $4,150",
            "#4482 Drain repair (Chen): $640",
          ],
          kranbosSource: "Jobber (completed jobs) + QuickBooks (invoices)",
          rows: [
            {
              label: "Where the data comes from",
              competitor: "Whatever someone copies or uploads into the chat",
              kranbos: "Pulled directly from your connected tools",
            },
            {
              label: "Stays up to date",
              competitor: "Only as fresh as the last paste",
              kranbos: "Refreshed automatically",
            },
            {
              label: "Works the same for the whole team",
              competitor: "Depends on how each person asks",
              kranbos: "Set up once, consistent for everyone",
            },
            {
              label: "Runs on a schedule",
              competitor: "You have to start every conversation",
              kranbos: "Weekly reports & reminders sent automatically",
            },
            {
              label: "Setup & upkeep",
              competitor: "Do it yourself",
              kranbos: "Done for you and managed monthly",
            },
          ],
          reasons: [
            {
              icon: "content_paste_off",
              title: "Copy-paste is the admin work",
              text: "If your office manager spends an hour prepping data for ChatGPT, you haven't saved the hour. You've moved it.",
            },
            {
              icon: "groups",
              title: "Your team needs the same answer",
              text: "Ten people asking in ten different ways get ten different results. Kranbos gives everyone the same source of truth.",
            },
            {
              icon: "schedule_send",
              title: "The best work happens without asking",
              text: "Unbilled-job alerts and Monday owner reports show up on their own. No one has to remember to open a chat.",
            },
          ],
          faqs: [
            {
              q: "We already pay for ChatGPT. Why add Kranbos?",
              a: "Keep using ChatGPT for writing and brainstorming. Kranbos handles the operational side: answering from your own records, connecting your tools, and running reports automatically.",
            },
            {
              q: "Can't ChatGPT connect to my apps?",
              a: "Some connections exist, but setting them up for your exact tools, permissions, and workflows, and keeping them working, is left to you. Kranbos builds and maintains that for you.",
            },
            {
              q: "Does my team have to change how they work?",
              a: "No. The agent works alongside the software and channels your team already uses.",
            },
          ],
          ctaTitle: "Stop copy-pasting. Start getting answers.",
          ctaText:
            "Book a $750 AI Operations Audit and we'll show you which daily tasks can run on their own, using the tools you already have.",
        },
        gemini: {
          path: "/vs-gemini",
          navLabel: "vs. Gemini",
          cardTitle: "Gemini vs. Kranbos",
          cardText: "How Kranbos adapts to your field software instead of fitting you into Google templates.",
          metaTitle: "Gemini vs. Kranbos | Custom AI Workflows for Field Service Businesses",
          metaDescription:
            "Gemini works best inside Google Workspace. Kranbos builds custom AI workflows around the field service tools you already use, like ServiceTitan, Jobber, and QuickBooks.",
          title: "Gemini fits Google's way of working. Kranbos fits yours.",
          subtitle:
            "Gemini shines inside Gmail, Docs, and Sheets. But your business doesn't run only in Google. Your jobs live in ServiceTitan or Jobber, your money lives in QuickBooks, and your crew lives in the field. Kranbos builds around all of it.",
          competitorName: "Gemini",
          summaryCompetitorTitle: "Gemini is great for",
          summaryCompetitorItems: [
            "Drafting in Gmail and Google Docs",
            "Summarizing files stored in Google Drive",
            "Teams that run almost entirely on Google Workspace",
          ],
          summaryKranbosTitle: "Kranbos is built for",
          summaryKranbosItems: [
            "Businesses that run on field service & accounting software",
            "Custom workflows designed around how your team works",
            "Connecting Google tools and everything else you use",
          ],
          scenarioQuestion: "Give me this week's summary for the Monday meeting.",
          competitorAnswer:
            "Here's a summary of the files and emails I can see in your Google Workspace. I don't have access to job or invoice data stored outside Google.",
          competitorNote: "Your jobs and invoices live in other tools, so the summary is incomplete.",
          kranbosAnswer: "Here's your week at a glance (Mon–Fri):",
          kranbosBullets: [
            "42 jobs completed, 5 callbacks (down 12% vs. last week)",
            "Invoiced: $61,300 · Outstanding over 30 days: $8,900",
            "Top crew by completed jobs: Team B (14)",
          ],
          kranbosSource: "ServiceTitan + QuickBooks + Google Drive",
          rows: [
            {
              label: "Works with field service software",
              competitor: "Designed around Google Workspace",
              kranbos: "ServiceTitan, Jobber, Housecall Pro & more",
            },
            {
              label: "Workflows",
              competitor: "Standard features and templates",
              kranbos: "Custom-built around your process",
            },
            {
              label: "Change how you work?",
              competitor: "Works best if you move more into Google",
              kranbos: "No. It adapts to your current tools",
            },
            {
              label: "Reports across all your systems",
              competitor: "Mostly what's inside Google apps",
              kranbos: "Combines jobs, invoices, files & email",
            },
            {
              label: "Setup & upkeep",
              competitor: "Do it yourself",
              kranbos: "Done for you and managed monthly",
            },
          ],
          reasons: [
            {
              icon: "hub",
              title: "Your business runs on more than one platform",
              text: "Field apps, accounting, supplier portals, email, and PDFs. An agent that only sees one ecosystem only sees part of the picture.",
            },
            {
              icon: "tune",
              title: "Your process is your advantage",
              text: "You've refined how you dispatch, quote, and follow up. Kranbos builds around that instead of asking you to adopt someone else's template.",
            },
            {
              icon: "do_not_disturb_on",
              title: "No migration project",
              text: "You don't need to move files, switch email providers, or retrain your team. Kranbos plugs into what's already working.",
            },
          ],
          faqs: [
            {
              q: "We use Google Workspace. Does Kranbos work with it?",
              a: "Yes. Kranbos connects to Google Drive, Gmail, and Sheets alongside your field service and accounting software, so you get the full picture in one place.",
            },
            {
              q: "Should we stop using Gemini?",
              a: "Not at all. Gemini is useful for writing inside Google apps. Kranbos covers the operational workflows that span all of your tools.",
            },
            {
              q: "How long does it take to set up?",
              a: "Most implementations take a few weeks after the audit, with no downtime for your office or crews.",
            },
          ],
          ctaTitle: "Get an AI agent that fits the way you already work",
          ctaText:
            "Book a $750 AI Operations Audit. We'll review your tools and workflows and design an agent around them, not around a template.",
        },
      },
    },
  },
  es: {
    aiAudit: {
      cta: "Agenda tu Auditoría de Operaciones con IA ($750)",
      ctaShort: "Agendar Auditoría",
      modalTitle: "Agenda tu Auditoría de Operaciones con IA",
      modalSubtitle:
        "Cuéntanos un poco sobre tu negocio. Te responderemos en un día hábil para agendar tu auditoría.",
      nameLabel: "Tu nombre",
      businessLabel: "Nombre del negocio",
      tradeLabel: "Industria",
      tradeOptions: [
        "Construcción y Manufactura",
        "Servicios Legales",
        "Salud y Medicina",
        "Retail y E-Commerce",
        "Bienes Raíces y Administración de Propiedades",
        "Servicios Locales y Belleza",
        "Otra",
      ],
      teamSizeLabel: "Tamaño del equipo",
      teamSizeOptions: ["1–9 personas", "10–20 personas", "21–50 personas", "Más de 50 personas"],
      toolsLabel: "Herramientas que usas hoy",
      toolsPlaceholder: "ej. QuickBooks, HubSpot, Google Drive",
      headacheLabel: "Tu mayor dolor de cabeza administrativo",
      headachePlaceholder:
        "ej. Sacar los números semanales le lleva todo el lunes a mi encargada de oficina.",
      selectPlaceholder: "Seleccionar…",
      submit: "Enviar solicitud de auditoría",
      cancel: "Cancelar",
      close: "Cerrar",
      priceNote: "Auditoría de Operaciones con IA: $750 pago único. Sin compromiso de trabajo adicional.",
      sentTitle: "Tu aplicación de correo debería abrirse",
      sentText:
        "Preparamos la solicitud por ti. Solo presiona enviar. Si no se abrió nada, escríbenos directamente a",
      done: "Listo",
      emailSubject: "Solicitud de Auditoría de Operaciones con IA",
      emailIntro: "Hola Kranbos, quiero agendar una Auditoría de Operaciones con IA.",
    },
    aiSimulator: {
      windowTitle: "Vista previa del Agente Kranbos",
      suggestionsLabel: "Prueba una pregunta de ejemplo:",
      placeholder: "Pregunta sobre presupuestos, especificaciones o facturas…",
      send: "Enviar",
      typing: "Revisando tus archivos y herramientas…",
      userLabel: "Tú",
      agentLabel: "Agente Kranbos",
      sourceLabel: "Fuente",
      exampleNote: "Datos de negocio de ejemplo con fines ilustrativos.",
      fallback:
        "Esta vista previa solo responde las preguntas de ejemplo de arriba. En tu negocio, el agente responde con tus propios documentos, listas de precios y software, y siempre muestra de dónde sale cada respuesta.",
      scenarios: [
        {
          id: "warranty",
          chip: "Consulta de garantía",
          question:
            "¿Cuál es la garantía de las instalaciones comerciales de HVAC en nuestro contrato de servicio 2024?",
          answer: "Según la sección 4.2 de tu Contrato de Servicio Comercial 2024:",
          bullets: [
            "Piezas y mano de obra: cobertura total por 2 años",
            "Compresor: garantía limitada de 5 años",
            "Requiere la visita de mantenimiento anual registrada",
          ],
          followUp: "",
          source: "Google Drive › Contratos › Contrato de Servicio Comercial 2024.pdf",
          keywords: ["garantía", "garantia", "cobertura"],
        },
        {
          id: "unbilled",
          chip: "Trabajos sin facturar",
          question: "¿Qué trabajos terminados la semana pasada todavía no se facturaron?",
          answer: "3 trabajos completados la semana pasada aún no tienen factura en QuickBooks:",
          bullets: [
            "#4471 Reemplazo de calentador de agua (Rivera): $2,380",
            "#4475 Actualización de tablero (Oak St. Dental): $4,150",
            "#4482 Reparación de desagüe (Chen): $640",
          ],
          followUp: "Total sin facturar: $7,170. ¿Quieres que prepare las facturas?",
          source: "Jobber (trabajos completados) + QuickBooks (facturas)",
          keywords: ["factura", "facturar", "facturado", "facturaron", "cobrar"],
        },
        {
          id: "report",
          chip: "Reporte semanal",
          question: "Dame el resumen de esta semana para la reunión del lunes.",
          answer: "Así fue tu semana (lun–vie):",
          bullets: [
            "42 trabajos completados, 5 revisitas (12% menos que la semana pasada)",
            "Facturado: $61,300 · Pendiente a más de 30 días: $8,900",
            "Mejor equipo por trabajos completados: Equipo B (14)",
          ],
          followUp: "El reporte completo llega a tu correo cada lunes a las 7:00 AM.",
          source: "ServiceTitan + QuickBooks",
          keywords: ["reporte", "resumen", "semana", "semanal", "números", "reunión"],
        },
        {
          id: "pricing",
          chip: "Lista de precios",
          question: "¿Cuánto cobramos por cambiar un calentador de 50 galones con retiro del viejo?",
          answer: "Según tu Lista de Precios Residencial 2024:",
          bullets: [
            "Calentador a gas de 50 gal, instalado: $1,850",
            "Retiro y desecho: $95",
            "Permiso (si aplica): se cobra al costo",
          ],
          followUp: "Los miembros del plan de mantenimiento tienen 10% de descuento en mano de obra.",
          source: "Google Drive › Precios › Lista de Precios Residencial 2024.xlsx",
          keywords: ["precio", "precios", "cobramos", "costo", "presupuesto", "calentador"],
        },
      ],
    },
    aiHome: {
      hero: {
        beforeTitle: "Trabajo Manual y Caos",
        beforeAlt:
          "Un dueño de negocio estresado en un escritorio desordenado con hojas de cálculo caóticas, papeles sueltos y notas adhesivas",
        afterTitle: "Flujo de Trabajo Automatizado",
        afterAlt:
          "El mismo dueño de negocio relajado con un café, mirando un panel limpio de Kranbos con cotizaciones generadas y tareas automatizadas completadas",
        title: "Deja de copiar y pegar, calcular a mano y buscar entre archivos.",
        titleAccent: "buscar entre archivos.",
        badge: "IA a medida entrenada con los datos de tu negocio",
        subtitle:
          "Creamos IA a medida que se conecta a tus archivos internos y tu software, y se encarga automáticamente de cotizaciones, reportes y tareas administrativas.",
      },
      industries: {
        title: "Creado para negocios en crecimiento de cualquier industria",
        titleAccent: "cualquier industria",
        items: [
          { icon: "construction", label: "Construcción y Manufactura" },
          { icon: "gavel", label: "Estudios Jurídicos y Servicios Legales" },
          { icon: "medical_services", label: "Consultorios Médicos y de Salud" },
          { icon: "storefront", label: "Retail y E-Commerce" },
          { icon: "apartment", label: "Bienes Raíces y Administración de Propiedades" },
          { icon: "spa", label: "Servicios Locales y Belleza" },
        ],
      },
      value: {
        title:
          "La IA genérica te da respuestas genéricas. Kranbos crea IA entrenada con tu negocio.",
        eyebrow: "Por qué Kranbos",
        titleAccent: "entrenada con tu negocio.",
        subtitle:
          "La IA genérica busca en internet.\nKranbos crea IA entrenada exclusivamente con tus archivos internos.",
        featureHeader: "Característica",
        genericHeader: "IA Genérica (como ChatGPT)",
        kranbosHeader: "Solución a Medida de Kranbos",
        rows: [
          {
            feature: "Fuente de datos",
            generic: "Información pública de la web (a menudo genérica o incorrecta para tu negocio)",
            kranbos: "Tus archivos, hojas de cálculo, PDFs y software interno reales",
          },
          {
            feature: "Tipo de resultado",
            generic: "Respuestas de texto en un chat",
            kranbos: "Hojas de cálculo terminadas, cotizaciones en PDF, reportes a medida y formularios web",
          },
          {
            feature: "Encaje en tu flujo",
            generic: "Requiere escribir instrucciones a mano cada vez",
            kranbos: "Se activa automáticamente dentro de tus operaciones diarias",
          },
          {
            feature: "Privacidad de datos",
            generic: "Entrenamiento en la nube pública",
            kranbos: "Estrictamente privada, segura y aislada para tu negocio",
          },
        ],
        privacyNote:
          "Cada solución de Kranbos se entrena en un entorno seguro y privado, aislado para tu negocio. Tus datos nunca se comparten con otros clientes ni se usan para entrenar modelos de IA públicos.",
      },
      useCases: {
        title: "Cómo los negocios usan Kranbos para ahorrar más de 10 horas por semana",
        eyebrow: "Casos de uso",
        titleAccent: "más de 10 horas por semana",
        items: [
          {
            icon: "request_quote",
            title: "Cotizaciones y Sincronización con CRM",
            tag: "Ahorra 5+ h/sem",
            points: [
              "Lee automáticamente tus listas de precios y completa los formularios de clientes.",
              "Se conecta directamente a tu CRM (HubSpot, Salesforce, Jobber) para generar cotizaciones en PDF al instante.",
            ],
          },
          {
            icon: "query_stats",
            title: "Reportes Ejecutivos Automáticos",
            tag: "Información al Instante",
            points: [
              "Toma datos en vivo de tus bases de datos de proyectos de años anteriores.",
              "Ajusta automáticamente por inflación, complejidad del proyecto y tipos de cambio.",
            ],
          },
          {
            icon: "manage_search",
            title: "Búsqueda en Archivos Internos",
            tag: "Cero Tiempo de Búsqueda",
            points: [
              "Convierte contratos, procedimientos y registros en una base de búsqueda privada.",
              "Obtén respuestas instantáneas y verificadas, tomadas solo de tus archivos.",
            ],
          },
        ],
      },
      howItWorks: {
        title: "De hojas de cálculo desordenadas a IA a medida en 4 simples pasos",
        eyebrow: "Cómo funciona",
        titleAccent: "en 4 simples pasos",
        stepLabel: "Paso",
        steps: [
          {
            icon: "cable",
            title: "Conecta tu Software y Documentos",
            text: "Conectamos Kranbos a las herramientas que ya usas: Google Drive, Excel, CRMs, PDFs y bases de datos. Sin migraciones ni cambios de sistema.",
          },
          {
            icon: "fact_check",
            title: "Entrenamos y Auditamos tu IA Privada",
            text: "Indexamos tus datos en una base de datos aislada, para que la IA solo use información verificada de tus registros reales.",
          },
          {
            icon: "account_tree",
            title: "Construcción de Flujos a Medida",
            text: "Configuramos tus disparadores a medida: generar cotizaciones desde formularios, correr reportes históricos o habilitar la búsqueda interna.",
          },
          {
            icon: "groups",
            title: "Capacitación del Equipo y Entrega",
            text: "Capacitamos a tu equipo con instrucciones simples y claras para que todos ahorren horas desde el primer día.",
          },
        ],
      },
      security: {
        title: "Los datos de tu negocio se mantienen 100% privados y seguros",
        eyebrow: "Seguridad y privacidad",
        titleAccent: "100% privados y seguros",
        items: [
          {
            icon: "block",
            title: "Cero Entrenamiento de Modelos Públicos",
            text: "Tus archivos internos y listas de precios NUNCA se usan para entrenar modelos de IA públicos como ChatGPT.",
          },
          {
            icon: "cloud_lock",
            title: "Nube Privada Aislada",
            text: "Tus datos viven en una base de datos segura y cifrada, dedicada exclusivamente a tu empresa.",
          },
          {
            icon: "admin_panel_settings",
            title: "Controles de Acceso Estrictos",
            text: "Tú decides exactamente quién de tu equipo tiene permiso para acceder a cada dato.",
          },
        ],
      },
      faq: {
        title: "Preguntas Frecuentes",
        eyebrow: "Preguntas frecuentes",
        titleAccent: "Preguntas",
        items: [
          {
            q: "¿Mi equipo tendrá que aprender herramientas técnicas complicadas?",
            a: "Para nada. Si tu equipo puede completar un formulario web, puede usar Kranbos. Nosotros nos encargamos de toda la configuración técnica.",
          },
          {
            q: "¿Y si nuestras hojas de cálculo o archivos están desordenados?",
            a: "Es normal. Durante nuestra Auditoría Profunda de Operaciones con IA, limpiamos, organizamos y estructuramos tus datos para lograr un 100% de precisión.",
          },
          {
            q: "¿Tenemos que reemplazar el software que ya usamos?",
            a: "¡No! Kranbos se conecta directamente a las herramientas que ya usas (HubSpot, Jobber, Excel, Google Workspace, etc.).",
          },
          {
            q: "¿Cuánto tarda el proceso de implementación?",
            a: "La mayoría de las implementaciones a medida quedan construidas, probadas y entregadas en 2 a 3 semanas.",
          },
        ],
      },
      engagement: {
        title: "Precios simples y predecibles para automatizar las operaciones de tu negocio",
        eyebrow: "Precios",
        titleAccent: "predecibles",
        featured: "Servicio principal",
        phases: [
          {
            label: "Paso 1",
            title: "Auditoría Profunda de Operaciones con IA",
            price: "$750",
            priceNote: "100% acreditado a tu implementación",
            text: "Una revisión práctica de tus cuellos de botella manuales, archivos y software, entregada como un plano de automatización a medida.",
            features: [
              "Mapeo detallado de procesos",
              "Plano de IA a medida y retorno proyectado",
              "$750 acreditados por completo a tu implementación",
            ],
            cta: "Agendar Auditoría",
          },
          {
            label: "Paso 2",
            title: "Implementación a Medida",
            price: "$3,500 – $7,500",
            priceNote: "Desarrollo de pago único",
            text: "Construimos, probamos e integramos tu solución a medida usando los archivos, datos de precios y software de tu empresa.",
            features: [
              "Flujos a medida construidos con tus datos",
              "Integrado con tus documentos, planillas y bases de datos",
              "Pruebas completas y capacitación del equipo",
            ],
            cta: "Agendar una Llamada de Servicio",
          },
          {
            label: "Paso 3",
            title: "Sistema Gestionado y Soporte",
            price: "$950 / mes",
            priceNote: "Continuo",
            text: "Mantenemos tu sistema rápido, seguro y actualizado a medida que tu negocio crece y tus archivos aumentan.",
            features: [
              "Sincronización continua a medida que creces",
              "Mantenimiento y optimización del rendimiento",
              "Soporte prioritario y actualizaciones periódicas",
            ],
            cta: "Gestionar tu Plan",
          },
        ],
        implementationSubject: "Llamada de servicio de Implementación a Medida",
        retainerSubject: "Plan de Sistema Gestionado y Soporte",
      },
      finalCta: {
        title: "Descubre qué tareas puede quitarte de encima la IA a medida.",
        titleAccent: "quitarte de encima",
        text: "Agenda una auditoría de operaciones rápida de 20 minutos. Revisaremos tu software actual y te mostraremos dónde se puede eliminar por completo el trabajo manual.",
      },
    },
    comparisons: {
      hubEyebrow: "Por qué Kranbos",
      hubTitle: "¿Cómo se compara Kranbos?",
      hubSubtitle:
        "Descubre por qué las herramientas de IA genéricas se quedan cortas para las operaciones diarias de un pequeño negocio.",
      readMore: "Ver comparación",
      otherTitle: "Más comparaciones",
      footerTitle: "Comparaciones",
      eyebrow: "Comparación",
      seeDifference: "Ver la diferencia",
      summaryTitle: "La respuesta corta",
      scenarioEyebrow: "La misma pregunta, dos respuestas",
      askedBy: "Tu técnico pregunta",
      kranbosLabel: "Agente Kranbos",
      tableTitle: "Lado a lado",
      tableFeature: "Lo que importa",
      tableKranbos: "Kranbos",
      reasonsTitle: "Por qué importa para tu negocio",
      faqTitle: "Preguntas frecuentes",
      ctaEyebrow: "Siguiente paso",
      backHome: "Volver al inicio",
      exampleNote: "Ejemplo ilustrativo con datos de muestra.",
      disclaimer:
        "ChatGPT es una marca registrada de OpenAI. Gemini y Google Workspace son marcas registradas de Google LLC. Kranbos es independiente y no está afiliado ni respaldado por ninguna de esas empresas.",
      pages: {
        genericAi: {
          path: "/vs-generic-ai",
          navLabel: "vs. Modelos de IA Genéricos",
          cardTitle: "Modelos de IA Genéricos vs. Kranbos",
          cardText: "Por qué la IA genérica da respuestas amplias en lugar de los datos exactos de tu negocio.",
          metaTitle: "IA Genérica vs. Kranbos | Agentes de IA a Medida para Pequeños Negocios",
          metaDescription:
            "Los modelos de IA genéricos no conocen tus precios, contratos ni historial de trabajos. Mira cómo un agente de Kranbos responde con tus propios documentos y herramientas.",
          title: "La IA genérica conoce internet. Kranbos conoce tu negocio.",
          subtitle:
            "Los modelos de IA generales se entrenan con información pública. Nunca vieron tu lista de precios, tus contratos de servicio ni los trabajos del mes pasado, así que llenan los huecos con suposiciones muy seguras.",
          competitorName: "IA genérica",
          summaryCompetitorTitle: "La IA genérica sirve para",
          summaryCompetitorItems: [
            "Redactar un correo o publicación general",
            "Explicar un concepto de la industria",
            "Generar ideas de marketing",
          ],
          summaryKranbosTitle: "Kranbos está hecho para",
          summaryKranbosItems: [
            "Respuestas basadas en tus propios documentos y registros",
            "Datos en vivo de las herramientas que ya usa tu equipo",
            "Mostrar exactamente de dónde sale cada respuesta",
          ],
          scenarioQuestion:
            "¿Cuál es nuestra garantía para una unidad comercial de techo que instalamos en 2024?",
          competitorAnswer:
            "Las unidades comerciales de techo suelen tener 1 año de garantía de mano de obra y de 5 a 10 años en piezas, según el fabricante. Revisa tu documentación para más detalles.",
          competitorNote: "Suena razonable, pero es un promedio de la industria, no tu contrato.",
          kranbosAnswer: "Según la sección 4.2 de tu Contrato de Servicio Comercial 2024:",
          kranbosBullets: [
            "Piezas y mano de obra: cobertura total por 2 años",
            "Compresor: garantía limitada de 5 años",
          ],
          kranbosSource: "Google Drive › Contratos › Contrato de Servicio Comercial 2024.pdf",
          rows: [
            {
              label: "Conoce tus precios, contratos y políticas",
              competitor: "No. Responde con información pública general",
              kranbos: "Sí. Responde con tus propios documentos",
            },
            {
              label: "Ve tus trabajos, facturas y agenda en vivo",
              competitor: "Sin acceso a tu software",
              kranbos: "Conectado a las herramientas que ya usas",
            },
            {
              label: "Muestra de dónde sale cada respuesta",
              competitor: "Rara vez, y no puede señalar tus archivos",
              kranbos: "Cada respuesta cita el archivo o registro",
            },
            {
              label: "Cuando no sabe",
              competitor: "Suele adivinar con confianza",
              kranbos: "Lo dice y te indica quién puede ayudar",
            },
            {
              label: "Configurado para tu equipo y oficio",
              competitor: "Talla única",
              kranbos: "Construido alrededor de tus flujos",
            },
          ],
          reasons: [
            {
              icon: "gpp_maybe",
              title: "Las respuestas equivocadas cuestan dinero",
              text: "Una garantía o un precio inventado en el sitio se convierte en un reembolso, una revisita o un cliente molesto.",
            },
            {
              icon: "folder_managed",
              title: "Tu conocimiento ya está escrito",
              text: "Contratos, listas de precios, manuales y notas de trabajo tienen las respuestas. Kranbos las hace fáciles de encontrar al instante.",
            },
            {
              icon: "verified",
              title: "Confianza que puedes verificar",
              text: "Como cada respuesta muestra su fuente, tu equipo puede verificarla con un toque en lugar de llamar a la oficina.",
            },
          ],
          faqs: [
            {
              q: "¿No puedo simplemente subir mis documentos a una IA genérica?",
              a: "Puedes para preguntas puntuales, pero alguien tiene que seguir subiendo las versiones nuevas, y cada persona obtiene resultados distintos. Kranbos mantiene tus documentos y software conectados y actualizados para todo el equipo.",
            },
            {
              q: "¿Los datos de mi negocio se usan para entrenar modelos públicos?",
              a: "No. Tu agente se configura solo para tu negocio, y tus documentos y registros no se usan para entrenar modelos públicos.",
            },
            {
              q: "¿Mis técnicos tienen que aprender una app nueva?",
              a: "No. Entregamos el agente donde tu equipo ya trabaja, como mensajes, chat o correo, así que no hay nada nuevo que aprender.",
            },
          ],
          ctaTitle: "Obtén respuestas de tu negocio, no de internet",
          ctaText:
            "Empieza con una Auditoría de Operaciones con IA de $750. Mapearemos dónde pierde tiempo tu equipo y te mostraremos exactamente qué respondería un agente a medida.",
        },
        chatgpt: {
          path: "/vs-chatgpt",
          navLabel: "vs. ChatGPT",
          cardTitle: "ChatGPT vs. Kranbos",
          cardText: "Por qué copiar y pegar en ChatGPT no alcanza para el trabajo diario de un equipo.",
          metaTitle: "ChatGPT vs. Kranbos | Agentes de IA Conectados a tus Herramientas",
          metaDescription:
            "ChatGPT es excelente para tareas puntuales, pero copiar y pegar datos todos los días no escala. Mira cómo Kranbos se conecta en vivo con QuickBooks, Jobber, ServiceTitan y más.",
          title: "ChatGPT es un gran asistente. Pero no está conectado a tu negocio.",
          subtitle:
            "Para obtener una respuesta útil de ChatGPT, alguien tiene que encontrar los datos, copiarlos de tu software, pegarlos y revisar el resultado. Todos los días. Kranbos se conecta directamente a tus herramientas, así que las respuestas ya están ahí.",
          competitorName: "ChatGPT",
          summaryCompetitorTitle: "ChatGPT es excelente para",
          summaryCompetitorItems: [
            "Escribir y pulir correos puntuales",
            "Resumir un documento que pegas",
            "Investigar y generar ideas de forma individual",
          ],
          summaryKranbosTitle: "Kranbos está hecho para",
          summaryKranbosItems: [
            "Operaciones diarias del equipo, no chats sueltos",
            "Conexiones en vivo con tus herramientas de trabajos, contabilidad y archivos",
            "Respuestas consistentes para todo el equipo",
          ],
          scenarioQuestion: "¿Qué trabajos terminados la semana pasada todavía no se facturaron?",
          competitorAnswer:
            "No tengo acceso a tus sistemas de gestión de trabajos ni de contabilidad. Si exportas los trabajos completados de la semana pasada y tu lista de facturas y los pegas aquí, puedo compararlos.",
          competitorNote: "Ahora alguien tiene que exportar dos reportes, pegarlos y revisar el resultado.",
          kranbosAnswer: "3 trabajos completados la semana pasada aún no tienen factura en QuickBooks:",
          kranbosBullets: [
            "#4471 Reemplazo de calentador de agua (Rivera): $2,380",
            "#4475 Actualización de tablero (Oak St. Dental): $4,150",
            "#4482 Reparación de desagüe (Chen): $640",
          ],
          kranbosSource: "Jobber (trabajos completados) + QuickBooks (facturas)",
          rows: [
            {
              label: "De dónde salen los datos",
              competitor: "De lo que alguien copia o sube al chat",
              kranbos: "Directo de tus herramientas conectadas",
            },
            {
              label: "Se mantiene actualizado",
              competitor: "Solo tan fresco como lo último que pegaste",
              kranbos: "Se actualiza automáticamente",
            },
            {
              label: "Funciona igual para todo el equipo",
              competitor: "Depende de cómo pregunta cada persona",
              kranbos: "Se configura una vez, consistente para todos",
            },
            {
              label: "Funciona de forma programada",
              competitor: "Tienes que iniciar cada conversación",
              kranbos: "Reportes y recordatorios semanales automáticos",
            },
            {
              label: "Configuración y mantenimiento",
              competitor: "Hazlo tú mismo",
              kranbos: "Lo hacemos por ti y lo gestionamos cada mes",
            },
          ],
          reasons: [
            {
              icon: "content_paste_off",
              title: "Copiar y pegar es el trabajo administrativo",
              text: "Si tu encargada de oficina pasa una hora preparando datos para ChatGPT, no ahorraste la hora. Solo la moviste.",
            },
            {
              icon: "groups",
              title: "Tu equipo necesita la misma respuesta",
              text: "Diez personas preguntando de diez formas distintas obtienen diez resultados distintos. Kranbos le da a todos la misma fuente de verdad.",
            },
            {
              icon: "schedule_send",
              title: "El mejor trabajo ocurre sin tener que pedirlo",
              text: "Las alertas de trabajos sin facturar y el reporte del lunes llegan solos. Nadie tiene que acordarse de abrir un chat.",
            },
          ],
          faqs: [
            {
              q: "Ya pagamos ChatGPT. ¿Por qué sumar Kranbos?",
              a: "Sigue usando ChatGPT para escribir e idear. Kranbos se ocupa del lado operativo: responder con tus propios registros, conectar tus herramientas y generar reportes automáticamente.",
            },
            {
              q: "¿ChatGPT no puede conectarse a mis apps?",
              a: "Existen algunas conexiones, pero configurarlas para tus herramientas, permisos y flujos exactos, y mantenerlas funcionando, queda a tu cargo. Kranbos lo construye y lo mantiene por ti.",
            },
            {
              q: "¿Mi equipo tiene que cambiar su forma de trabajar?",
              a: "No. El agente funciona junto al software y los canales que tu equipo ya usa.",
            },
          ],
          ctaTitle: "Deja de copiar y pegar. Empieza a obtener respuestas.",
          ctaText:
            "Agenda una Auditoría de Operaciones con IA de $750 y te mostraremos qué tareas diarias pueden funcionar solas, con las herramientas que ya tienes.",
        },
        gemini: {
          path: "/vs-gemini",
          navLabel: "vs. Gemini",
          cardTitle: "Gemini vs. Kranbos",
          cardText: "Cómo Kranbos se adapta a tu software de campo en lugar de encajarte en plantillas de Google.",
          metaTitle: "Gemini vs. Kranbos | Flujos de IA a Medida para Servicios de Campo",
          metaDescription:
            "Gemini funciona mejor dentro de Google Workspace. Kranbos crea flujos de IA a medida alrededor de las herramientas de campo que ya usas, como ServiceTitan, Jobber y QuickBooks.",
          title: "Gemini se adapta a la forma de trabajar de Google. Kranbos, a la tuya.",
          subtitle:
            "Gemini brilla dentro de Gmail, Docs y Sheets. Pero tu negocio no funciona solo en Google. Tus trabajos viven en ServiceTitan o Jobber, tu dinero en QuickBooks y tu equipo en el campo. Kranbos se construye alrededor de todo eso.",
          competitorName: "Gemini",
          summaryCompetitorTitle: "Gemini es excelente para",
          summaryCompetitorItems: [
            "Redactar en Gmail y Google Docs",
            "Resumir archivos guardados en Google Drive",
            "Equipos que trabajan casi todo en Google Workspace",
          ],
          summaryKranbosTitle: "Kranbos está hecho para",
          summaryKranbosItems: [
            "Negocios que funcionan con software de servicios de campo y contabilidad",
            "Flujos a medida diseñados según cómo trabaja tu equipo",
            "Conectar las herramientas de Google y todo lo demás que usas",
          ],
          scenarioQuestion: "Dame el resumen de esta semana para la reunión del lunes.",
          competitorAnswer:
            "Aquí tienes un resumen de los archivos y correos que puedo ver en tu Google Workspace. No tengo acceso a datos de trabajos o facturas guardados fuera de Google.",
          competitorNote: "Tus trabajos y facturas viven en otras herramientas, así que el resumen queda incompleto.",
          kranbosAnswer: "Así fue tu semana (lun–vie):",
          kranbosBullets: [
            "42 trabajos completados, 5 revisitas (12% menos que la semana pasada)",
            "Facturado: $61,300 · Pendiente a más de 30 días: $8,900",
            "Mejor equipo por trabajos completados: Equipo B (14)",
          ],
          kranbosSource: "ServiceTitan + QuickBooks + Google Drive",
          rows: [
            {
              label: "Funciona con software de servicios de campo",
              competitor: "Diseñado alrededor de Google Workspace",
              kranbos: "ServiceTitan, Jobber, Housecall Pro y más",
            },
            {
              label: "Flujos de trabajo",
              competitor: "Funciones y plantillas estándar",
              kranbos: "Construidos a medida de tu proceso",
            },
            {
              label: "¿Cambiar tu forma de trabajar?",
              competitor: "Funciona mejor si pasas más cosas a Google",
              kranbos: "No. Se adapta a tus herramientas actuales",
            },
            {
              label: "Reportes con todos tus sistemas",
              competitor: "Mayormente lo que está dentro de Google",
              kranbos: "Combina trabajos, facturas, archivos y correo",
            },
            {
              label: "Configuración y mantenimiento",
              competitor: "Hazlo tú mismo",
              kranbos: "Lo hacemos por ti y lo gestionamos cada mes",
            },
          ],
          reasons: [
            {
              icon: "hub",
              title: "Tu negocio funciona en más de una plataforma",
              text: "Apps de campo, contabilidad, portales de proveedores, correo y PDFs. Un agente que solo ve un ecosistema solo ve parte del panorama.",
            },
            {
              icon: "tune",
              title: "Tu proceso es tu ventaja",
              text: "Perfeccionaste cómo despachas, cotizas y das seguimiento. Kranbos se construye alrededor de eso en lugar de pedirte adoptar la plantilla de otro.",
            },
            {
              icon: "do_not_disturb_on",
              title: "Sin proyecto de migración",
              text: "No necesitas mover archivos, cambiar de proveedor de correo ni volver a capacitar a tu equipo. Kranbos se conecta a lo que ya funciona.",
            },
          ],
          faqs: [
            {
              q: "Usamos Google Workspace. ¿Kranbos funciona con eso?",
              a: "Sí. Kranbos se conecta a Google Drive, Gmail y Sheets junto con tu software de servicios de campo y contabilidad, para que tengas el panorama completo en un solo lugar.",
            },
            {
              q: "¿Deberíamos dejar de usar Gemini?",
              a: "Para nada. Gemini es útil para escribir dentro de las apps de Google. Kranbos cubre los flujos operativos que abarcan todas tus herramientas.",
            },
            {
              q: "¿Cuánto tarda la implementación?",
              a: "La mayoría de las implementaciones toman unas pocas semanas después de la auditoría, sin interrupciones para tu oficina ni tus equipos.",
            },
          ],
          ctaTitle: "Obtén un agente de IA que se adapte a como ya trabajas",
          ctaText:
            "Agenda una Auditoría de Operaciones con IA de $750. Revisaremos tus herramientas y flujos y diseñaremos un agente alrededor de ellos, no de una plantilla.",
        },
      },
    },
  },
};
